import React, { useEffect, useState } from "react";
import { FileDown, ShieldCheck, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { AiFeatureMap } from "@/components/AiFeatureMap";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const Row = ({ label, value, good }) => (
  <div className="flex items-start justify-between gap-4 border-b border-slate-100 py-2 last:border-0">
    <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</span>
    <span className={`text-right text-sm ${good === true ? "font-semibold text-emerald-700" : good === false ? "font-semibold text-amber-700" : "text-slate-700"}`}>
      {value}
    </span>
  </div>
);

export default function AdminAssurance() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/ai/assurance").then(({ data }) => setA(data))
      .catch((e) => toast.error(apiError(e.response?.data?.detail) || "Could not load the assurance statement"));
  }, []);

  const download = async () => {
    setBusy(true);
    try {
      const res = await api.get("/ai/assurance.pdf", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "ai-assurance-statement.pdf";
      link.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Could not generate the PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell
      testId="admin-assurance-page"
      title="AI Assurance Statement"
      subtitle="A live, evidence-backed statement of where AI processing happens and what is redacted before it. Export it and hand it straight to a client's IT or information governance team."
      actions={
        <Button data-testid="assurance-pdf-btn" disabled={busy} onClick={download} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} />} Export PDF
        </Button>
      }
    >
      {!a ? (
        <p className="py-8 text-center text-sm text-slate-400">Loading statement…</p>
      ) : (
        <div className="space-y-6">
          <section className={`panel p-5 ${a.tenant_bound ? "border-l-4 border-l-emerald-500" : "border-l-4 border-l-amber-500"}`} data-testid="assurance-position">
            <h3 className="flex items-center gap-2 font-display text-base font-semibold text-slate-800">
              <ShieldCheck size={15} className={a.tenant_bound ? "text-emerald-600" : "text-amber-500"} /> Position for {a.org_name}
            </h3>
            <div className="mt-3">
              <Row label="AI engine" value={a.engine_label} good={a.tenant_bound} />
              <Row label="Tenant-bound" value={a.tenant_bound ? "Yes" : "No"} good={a.tenant_bound} />
              <Row label="Internal-AI-only lock" value={a.internal_only_locked ? "Locked on" : "Not locked"} good={a.internal_only_locked ? true : undefined} />
              <Row label="Data processor" value={a.processor} />
              <Row label="Redaction mode" value={a.egress_mode} good={a.egress_mode === "strict" ? true : undefined} />
              <Row label="Audit retention" value={`${a.retention_days} days`} />
              <Row label="Calls logged to date" value={a.audited_calls} />
            </div>
          </section>

          <section className="panel p-5" data-testid="assurance-boundary">
            <h3 className="font-display text-base font-semibold text-slate-800">Data boundary</h3>
            <p className="mt-2 text-sm text-slate-700">{a.data_boundary}</p>
          </section>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <section className="panel p-5" data-testid="assurance-redaction">
              <h3 className="font-display text-base font-semibold text-slate-800">Redaction before egress</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                {a.redaction_rules.map((r) => <li key={r} className="flex gap-2"><span className="text-emerald-600">✓</span>{r}</li>)}
              </ul>
            </section>
            <section className="panel p-5" data-testid="assurance-controls">
              <h3 className="font-display text-base font-semibold text-slate-800">Controls enforced in-application</h3>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-700">
                {a.controls.map((c) => <li key={c} className="flex gap-2"><span className="text-emerald-600">✓</span>{c}</li>)}
              </ul>
            </section>
          </div>

          <section className="panel p-5" data-testid="assurance-sample">
            <h3 className="font-display text-base font-semibold text-slate-800">Worked redaction example</h3>
            <p className="mt-1 text-xs text-slate-500">Run against your live redaction mode — this is exactly what happens to a payload.</p>
            <div className="mt-3 space-y-3">
              <div>
                <span className="field-label">Before</span>
                <p className="rounded bg-red-50 p-3 font-mono text-xs text-slate-700" data-testid="assurance-sample-before">{a.sample_before}</p>
              </div>
              <div>
                <span className="field-label">After — what actually leaves</span>
                <p className="rounded bg-emerald-50 p-3 font-mono text-xs text-slate-700" data-testid="assurance-sample-after">{a.sample_after}</p>
              </div>
            </div>
          </section>

          <AiFeatureMap testId="assurance-feature-map" />

          <p className="text-xs text-slate-500">
            Generated {String(a.generated_at).slice(0, 19).replace("T", " ")} by {a.generated_by}. This statement reflects
            the live configuration of this workspace at the time of generation.
          </p>
        </div>
      )}
    </Shell>
  );
}
